import React, { useState } from 'react';
import { ConfigProvider, Menu } from 'antd';
import type { DashboardMenuItem, DashboardMenuTheme } from './DashboardMenu';

import styles from './DashboardMenu.module.css';

interface DashboardMenuNavigationProps {
  items: DashboardMenuItem[];
  selectedKey?: string;
  isCollapsed: boolean;
  onNavigate: () => void;
  menuTheme?: DashboardMenuTheme;
}

const DashboardMenuNavigation: React.FC<DashboardMenuNavigationProps> = ({
  items,
  selectedKey,
  isCollapsed,
  onNavigate,
  menuTheme,
}) => {
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const handleClick = (item: DashboardMenuItem) => {
    item.onClick?.();
    onNavigate();
  };

  const isOnActivePath = (item: DashboardMenuItem): boolean =>
    item.key === selectedKey || !!item.children?.some(isOnActivePath);

  // Submenus holding the active item stay open whatever the hover state.
  const getActiveSubmenuKeys = (menuItems: DashboardMenuItem[]): string[] =>
    menuItems
      .filter((item) => item.children?.length && isOnActivePath(item))
      .flatMap((item) => [item.key, ...getActiveSubmenuKeys(item.children ?? [])]);

  const renderLabel = (item: DashboardMenuItem) => {
    const label = item.href ? (
      <a href={item.href} target="_blank" rel="noopener noreferrer">
        {item.title}
      </a>
    ) : (
      item.title
    );

    if (!item.badge) return label;

    return (
      <span className={styles['navigationItemLabel']}>
        <span>{label}</span>
        {item.badge}
      </span>
    );
  };

  const renderItem = (item: DashboardMenuItem) => {
    const isActive = selectedKey === item.key;
    const icon = isActive ? item.iconActive ?? item.icon : item.icon;

    if (item.children && item.children.length > 0) {
      return (
        <Menu.SubMenu
          key={item.key}
          icon={icon}
          disabled={item.disabled}
          // Open on hover and close on leave, the li wraps the title and its children.
          onMouseEnter={() =>
            setOpenKeys((prev) => (prev.includes(item.key) ? prev : [...prev, item.key]))
          }
          onMouseLeave={() => setOpenKeys((prev) => prev.filter((key) => key !== item.key))}
          title={
            <span
              className={styles['navigationSubmenuTitle']}
              onClick={(event) => {
                // Prevent title click from toggling submenu open state.
                event.preventDefault();
                event.stopPropagation();
                handleClick(item);
              }}
            >
              {item.title}
            </span>
          }
        >
          {item.children.map(renderItem)}
        </Menu.SubMenu>
      );
    }

    return (
      <Menu.Item
        key={item.key}
        icon={icon}
        disabled={item.disabled}
        onClick={(e) => {
          e.domEvent?.preventDefault();
          handleClick(item);
        }}
      >
        {renderLabel(item)}
      </Menu.Item>
    );
  };

  const activeSubmenuKeys = getActiveSubmenuKeys(items);

  return (
    <ConfigProvider
      theme={{
        components: {
          Tooltip: {
            controlHeight: 32,
          },
          Menu: menuTheme,
        },
      }}
    >
      <Menu
        className={styles['navigationMenu']}
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          width: '100%',
        }}
        mode="inline"
        selectedKeys={selectedKey ? [selectedKey] : []}
        openKeys={isCollapsed ? [] : [...new Set([...activeSubmenuKeys, ...openKeys])]}
        // Keeps Enter on a submenu title working, forced active keys are left out of the hover state.
        onOpenChange={(keys) => setOpenKeys(keys.filter((key) => !activeSubmenuKeys.includes(key)))}
        inlineCollapsed={isCollapsed}
      >
        {items.flatMap((item) =>
          item.dividerBefore ? [<Menu.Divider key={`${item.key}-divider`} />, renderItem(item)] : [renderItem(item)]
        )}
      </Menu>
    </ConfigProvider>
  );
};

export default DashboardMenuNavigation;
